/**
 * Pure geometry/threshold logic for dragging a Dockview tab (DockLayer's
 * CustomTab). Kept side-effect-free so the axis discrimination and the
 * insertion-index math are unit-testable without a DOM or Dockview.
 *
 * The interaction model (Obsidian-style): a tab is a live editor you can shove
 * around. A small mostly-horizontal drag that stays WITHIN the tab-strip band
 * reorders the tab in place; a drag that pulls OUT of the strip vertically
 * detaches it into a floating window (the pre-existing behavior).
 */

export interface TabDragThresholds {
  /** px of horizontal travel before an in-strip reorder begins. */
  reorder?: number;
  /** px beyond the strip band (vertically) before the drag detaches to floating. */
  detach?: number;
}

export type TabDragMode = 'idle' | 'reorder' | 'detach';

/**
 * Classify a tab pointer-drag from its horizontal travel (`dx`, signed, px from
 * the grab point) and how far the pointer currently sits OUTSIDE the tab-strip
 * band vertically (`outsideBandY`, px, 0 while within the band).
 *
 * Axis-aware and detach-dominant: leaving the strip vertically always wins (you
 * are pulling the tab out), even mid-reorder; otherwise enough horizontal travel
 * starts a reorder; below both thresholds it is still just a click-in-progress.
 */
export function tabDragMode(
  dx: number,
  outsideBandY: number,
  t: TabDragThresholds = {},
): TabDragMode {
  const reorder = t.reorder ?? 6;
  const detach = t.detach ?? 24;
  if (outsideBandY > detach) return 'detach';
  if (Math.abs(dx) > reorder) return 'reorder';
  return 'idle';
}

/**
 * How far (px) a pointer at `clientY` sits outside a strip band spanning
 * [`bandTop`, `bandBottom`] — 0 while inside, otherwise the vertical distance
 * past the nearer edge. Feeds {@link tabDragMode}'s `outsideBandY`.
 */
export function bandExcess(clientY: number, bandTop: number, bandBottom: number): number {
  if (clientY < bandTop) return bandTop - clientY;
  if (clientY > bandBottom) return clientY - bandBottom;
  return 0;
}

/**
 * The index the dragged tab should occupy, given the pointer's x and the
 * horizontal centres of the OTHER tabs (in left-to-right visual order).
 *
 * It is exactly the count of other tabs whose centre is left of the pointer —
 * which is also the post-removal insertion index Dockview's `moveTo({index})`
 * wants: that call removes the panel, then inserts it at `index` among the
 * remaining tabs. So no off-by-one translation is needed.
 */
export function targetTabIndex(pointerX: number, otherCentres: number[]): number {
  let idx = 0;
  for (const cx of otherCentres) if (pointerX > cx) idx++;
  return idx;
}
